/*
 * @format
 * @flow
 */
import React, { type Node } from 'react';
import { SecurityHelper, PlaceholderText, Button, TYPES, Stack } from 'sarsaparilla';
import { get } from 'lodash';
import moment from 'moment';

import { type QueueLottery, type EapSeasonType } from 'ui-queuing-lottery'

import { type PermitType, type LotteryType } from '../types';
import { type AccountType } from '../types/account';
import LotteryWidget from '../lottery/LotteryWidget';
import QueuingLotteryWidget from '../lottery/QueuingLotteryWidget';

type ImportantDatesTypes = {
    handleImportantDates: Function,
};

type FutureLotteriesType = {
    permit: PermitType,
    enterLottery: Function,
    futureLotteriesList: Array<LotteryType>,
    hasTitle: boolean,
    isOldLotteryFormat?: boolean,
};

type RightRailTypes = {
    isDisabledButton?: boolean,
    isLoading: boolean,
    isLoadingButton?: boolean,
    isClosed: boolean,
    permit: PermitType,
    openLotteries?: LotteryType[],
    futureLotteriesList?: LotteryType[],
    openQueuingLotteries?: QueueLottery[],
    futureQueuingLotteries?: QueueLottery[],
    earlyAccessPeriods?: EapSeasonType[],
    handleImportantDates?: Function,
    handleEnterLottery?: Function,
    children?: Array<Node>,
    extraChildren?: Array<Node>,
    buttonList?: Array<Node>,
    history?: TYPES.HISTORY,
    showRentalPromo?: boolean,
    isOldLotteryFormat?: boolean,
    desktopDisabled?: boolean,
};

const FUTURE_LOTTERY_TITLE = 'Future Lottery';
const LOTTERY_OPEN_TITLE = 'Lottery Open';
const FUTURE_QUEUING_LOTTERY_TITLE = 'Future Lottery';
const QUEUING_LOTTERY_OPEN_TITLE = 'Early Access Lottery';
const AVAILABLE_PERMITS_TITLE = 'Available Permits';
const EARLY_ACCESS_PERIOD_TITLE = 'Early Access Period';

// Checks if account is commercial and has access to the permit
export function hasAccessToPermit(account: AccountType, permitId: string) {
    if (account.is_commercial_account) {
        const availablePermit = get(account, ['special_use_permits', permitId]);
        return (
            availablePermit &&
            !availablePermit.is_suspended &&
            moment(availablePermit.expiration).isAfter(moment())
        );
    }
    return true;
}

function ImportantDatesButton({ handleImportantDates }: ImportantDatesTypes) {
    return (
        <Button
            appearance="tertiary"
            shouldFitContainer
            onClick={handleImportantDates}
            screenReaderTextAfter="see important dates"
        >
            See Important Dates
        </Button>
    );
}

function FutureLotteries({
    permit,
    futureLotteriesList,
    enterLottery,
    hasTitle,
    isOldLotteryFormat,
}: FutureLotteriesType) {
    return (
            <LotteryWidget
                futureLotteryTitle={hasTitle ? FUTURE_LOTTERY_TITLE : ''}
                isFutureLottery
                enterLottery={enterLottery}
                permit={permit}
                lotteries={futureLotteriesList}
                isOldLotteryFormat={isOldLotteryFormat}
            />
    );
}

type FutureQueueLotteriesType = {
    permit: PermitType,
    enterLottery: Function,
    futureQueuingLotteries: QueueLottery[],
    hasTitle: boolean,
};

function FutureQueueLotteries({
    permit,
    futureQueuingLotteries,
    enterLottery,
    hasTitle,
}: FutureQueueLotteriesType) {
    return (
        <QueuingLotteryWidget
            futureLotteryTitle={hasTitle ? FUTURE_LOTTERY_TITLE : ''}
            isFutureLottery
            permit={permit}
            enterLottery={enterLottery}
            lotteries={futureQueuingLotteries}
        />
    );
}

function getTitle(isClosed, openLotteries, futureLotteriesList, openQueuingLotteries, futureQueuingLotteries, earlyAccessPeriods) {
    if (openLotteries.length) {
        return LOTTERY_OPEN_TITLE;
    }
    if (openQueuingLotteries.length) {
        return QUEUING_LOTTERY_OPEN_TITLE;
    }
    if(!isClosed) {
        // Open Season is higher priority than Future Lotteries
        return AVAILABLE_PERMITS_TITLE;
    }
    if (earlyAccessPeriods.length) {
        return EARLY_ACCESS_PERIOD_TITLE;
    }
    if (futureLotteriesList.length) {
        return FUTURE_LOTTERY_TITLE;
    }
    if (futureQueuingLotteries.length) {
        return FUTURE_QUEUING_LOTTERY_TITLE;
    }
    return AVAILABLE_PERMITS_TITLE;
}

export default function RightRailContainer({
    permit,
    isDisabledButton,
    isLoading,
    isLoadingButton,
    isClosed,
    openLotteries = [],
    futureLotteriesList = [],
    openQueuingLotteries = [],
    futureQueuingLotteries = [],
    handleEnterLottery,
    handleImportantDates,
    buttonList,
    children,
    extraChildren,
    history,
    showRentalPromo = true,
    earlyAccessPeriods = [],
    isOldLotteryFormat = false,
    desktopDisabled = false,
}: RightRailTypes) {
    const title = getTitle(isClosed, openLotteries, futureLotteriesList, openQueuingLotteries, futureQueuingLotteries, earlyAccessPeriods);
    const hasAnyLottery = openLotteries.length ||
        futureLotteriesList.length ||
        openQueuingLotteries.length ||
        futureQueuingLotteries.length ||
        earlyAccessPeriods.length

    if (isLoading) {
        return {
            title: 'Loading...',
            showRentalPromo: false,
            body: (
                <div className="py-2 right-rail-card">
                    <PlaceholderText lines={2} fontSize={19} lineHeight={23} />
                    <PlaceholderText lines={3} fontSize={19} lineHeight={23} />
                </div>
            ),
        };
    }

    if (!hasAccessToPermit(SecurityHelper.getAccount(), permit.id)) {
        return {
            title: 'Not Authorized',
            notAuth: true,
            links: permit?.links,
            body: (
                <div className="py-2 right-rail-card">
                    <p>
                        You are not authorized to make commercial reservations at this
                        location, please contact the facility for more information.
                    </p>
                </div>
            ),
        };
    }

    if (isClosed && !hasAnyLottery) {
        return {
            title: 'Registration Closed',
            links: permit?.links,
            body: (
                <div className="py-2 right-rail-card">
                    <p>Reservations have not been released yet.</p>
                    <p>
                        See the important dates section to view when registration opens.
                    </p>
                    {handleImportantDates && (
                        <ImportantDatesButton
                            handleImportantDates={handleImportantDates}
                        />
                    )}
                </div>
            ),
        };
    }

    const lotteryOpen = openLotteries?.length ? (
        <LotteryWidget
            key="LotteryWidget"
            permit={permit}
            lotteries={openLotteries}
            isDisabledButton={isDisabledButton}
            isLoadingButton={isLoadingButton}
            isOldLotteryFormat={isOldLotteryFormat}
            enterLottery={handleEnterLottery}
            history={history}
            desktopDisabled={desktopDisabled}
        />
    ) : null;

    const futureLotteries = futureLotteriesList?.length ? (
        <FutureLotteries
            key="FutureLotteryWidget"
            hasTitle={title !== FUTURE_LOTTERY_TITLE}
            permit={permit}
            futureLotteriesList={futureLotteriesList}
            isOldLotteryFormat={isOldLotteryFormat}
            enterLottery={handleEnterLottery}
        />
    ) : null;

    const openQueuingLottery = !!openQueuingLotteries?.length && (
        <QueuingLotteryWidget
            permit={permit}
            lotteries={openQueuingLotteries}
            onEnterLottery={handleEnterLottery}
        />
    );

    const futureQueuingLottery = !!futureQueuingLotteries?.length && (
        <FutureQueueLotteries
            key="FutureQueueLotteryWidget"
            hasTitle={title !== FUTURE_QUEUING_LOTTERY_TITLE}
            permit={permit}
            futureQueuingLotteries={futureQueuingLotteries}
            enterLottery={handleEnterLottery}
        />
    );

    const renderAvailablePermit = () => {
        const shouldRenderAvailablePermitHeader = !isClosed && !!lotteryOpen || !!openQueuingLottery;

        return children?.length || buttonList?.length ? (
            <div className="right-rail-card">
                {shouldRenderAvailablePermitHeader && (
                    <div className="rec-aside-subheader">
                        <div>{AVAILABLE_PERMITS_TITLE}</div>
                    </div>
                )}
                {children}
                {buttonList}
            </div>
        ) : null;
    }

    const widgets = [
        lotteryOpen,
        openQueuingLottery,
        renderAvailablePermit(),
        futureLotteries,
        futureQueuingLottery,
    ];

    return {
        title,
        links: permit?.links,
        showRentalPromo,
        body: (
            <Stack>
                {widgets}
                {extraChildren}
            </Stack>
        ),
    };
}
